'use client';

/**
 * AxisWeights — labeled weight grid with optional rationale prose.
 *
 * Cards-everywhere-native primitive. Takes a list of axis records
 * matching the `IntentSummary.pareto_axes` shape (`name`, `weight`,
 * `default`, `changed?`) and renders a 3-column grid: each cell holds
 * a large weight number over a small underscore-stripped axis label.
 * Changed axes (where current ≠ default) get a warning-tinted
 * background so operators can see what's been tuned at a glance —
 * hover-title on the cell shows the original default.
 *
 * Optional `rationale` prop renders italic prose between the label
 * and the grid. Mirrors the schema-driven `AXIS_WEIGHTS` section's
 * `fallback_field="pareto_rationale"` behavior — rationale belongs
 * with the weights it explains, not as a separate section.
 *
 * Auto-hides when the axis list is empty.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot / rail-insights
 */

import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';

export interface AxisWeight {
  name: string;
  weight: number;
  default: number;
  changed?: boolean;
}

interface AxisWeightsProps {
  axes: AxisWeight[] | null | undefined;
  rationale?: string | null;
  label?: string;
}

function formatWeight(w: number): string {
  return Number.isInteger(w) ? w.toString() : w.toFixed(1);
}

function formatAxisLabel(name: string): string {
  return name.replace(/_/g, ' ');
}

export function AxisWeights({ axes, rationale, label }: AxisWeightsProps) {  if (!axes || axes.length === 0) return null;
  return (
    <div>
      {label && <p className={text.eyebrow}>{label}</p>}
      {rationale && (
        <p className={cn(text.muted, 'italic leading-relaxed mb-2')}>
          {rationale}
        </p>
      )}
      <div className="grid grid-cols-3 gap-1.5">
        {axes.map((a) => (
          <div
            key={a.name}
            className={cn(
              'p-2 rounded-lg border text-center',
              a.changed
                ? 'bg-warning/10 border-warning/40'
                : 'bg-card border-border',
            )}
            title={a.changed ? `default ${a.default}` : undefined}
          >
            <div className={text.value}>{formatWeight(a.weight)}</div>
            <div className={text.muted}>{formatAxisLabel(a.name)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
